/**
 * Sélecteur d'objet courant avec recherche — WO-V5-A / US-029.1 (cible codex §4.2).
 *
 * Motif ARIA « combobox + listbox » rendu SANS portail ni bibliothèque de positionnement :
 * un bouton déclencheur (`aria-haspopup="listbox"`, `aria-expanded`), puis un panneau contenant
 * un champ de recherche (`role="combobox"`, `aria-activedescendant`) et une liste
 * (`role="listbox"` / `role="option"`). Clavier : ↓/↑ déplacent l'option active, Entrée
 * choisit, Échap ferme et RAMÈNE LE FOCUS sur le déclencheur. Le filtre est un simple
 * « contient » insensible à la casse et aux accents — les grands volumes se cherchent aussi
 * dans ⌘K, ce sélecteur situe, il n'explore pas.
 *
 * Pourquoi pas le `<select>` natif de `ContextSwitcher` : il n'a pas de recherche, et un
 * programme de plusieurs dizaines de projets s'y parcourt à l'aveugle.
 */
import { useEffect, useId, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react'
import { Check, ChevronDown, ChevronsUpDown, Search } from 'lucide-react'

import { useT } from '@/i18n/language'

export interface OptionObjet { id: string; label: string; detail?: string }

function normaliser(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export function SelecteurObjet({ options, courant, libelle, placeholder, onChoisir, desactive, motifDesactive, testid = 'contexte-objet', forme = 'bouton', symbole }: {
  options: OptionObjet[]
  courant: string | null
  /** Libellé du déclencheur quand rien n'est choisi (ex. « Choisir un projet »). */
  libelle: string
  placeholder: string
  onChoisir: (id: string) => void
  desactive?: boolean
  motifDesactive?: string
  testid?: string
  /** `puce` : sélecteur de la barre haute (planche BarreHaute, « ◆ Programme ▾ »). */
  forme?: 'bouton' | 'puce'
  symbole?: string
}): JSX.Element {
  const { t } = useT()
  const [ouvert, setOuvert] = useState(false)
  const [recherche, setRecherche] = useState('')
  const [actif, setActif] = useState(0)
  const declencheur = useRef<HTMLButtonElement>(null)
  const champ = useRef<HTMLInputElement>(null)
  const idListe = useId()
  const idAide = useId()

  const filtrees = useMemo(() => {
    const q = normaliser(recherche.trim())
    return q ? options.filter((o) => normaliser(o.label).includes(q) || normaliser(o.id).includes(q)) : options
  }, [options, recherche])

  const objetCourant = options.find((o) => o.id === courant) ?? null

  useEffect(() => { if (ouvert) champ.current?.focus() }, [ouvert])
  useEffect(() => { setActif(0) }, [recherche, ouvert])

  const fermer = (rendreFocus: boolean): void => {
    setOuvert(false)
    setRecherche('')
    if (rendreFocus) declencheur.current?.focus()
  }
  const choisir = (id: string): void => { onChoisir(id); fermer(true) }

  const auClavier = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActif((i) => Math.min(i + 1, Math.max(filtrees.length - 1, 0))) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActif((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); const o = filtrees[actif]; if (o) choisir(o.id) }
    else if (e.key === 'Escape') { e.preventDefault(); fermer(true) }
  }

  const idOption = (id: string): string => `${idListe}-${id}`

  return (
    <div className="relative min-w-0 max-w-full">
      <button ref={declencheur} type="button" data-testid={testid} data-courant={courant ?? ''}
              aria-haspopup="listbox" aria-expanded={ouvert} aria-controls={ouvert ? idListe : undefined}
              aria-describedby={desactive && motifDesactive ? idAide : undefined}
              aria-disabled={desactive ? 'true' : undefined} disabled={desactive}
              onClick={() => (ouvert ? fermer(true) : setOuvert(true))}
              title={objetCourant?.label}
              className={forme === 'puce'
                ? 'flex h-11 max-w-[15rem] items-center gap-1.5 rounded-lg border bg-card px-2.5 text-[0.8125rem] font-semibold hover:bg-secondary disabled:cursor-not-allowed'
                : 'flex h-10 max-w-full items-center gap-1.5 rounded-md border bg-background px-2 text-[0.8125rem] font-medium hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60'}>
        {symbole ? <span aria-hidden="true" className="shrink-0 text-[0.6875rem] text-brand-text">{symbole}</span> : null}
        <span className="min-w-0 truncate">{objetCourant?.label ?? libelle}</span>
        {forme === 'puce'
          ? <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          : <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
      </button>
      {desactive && motifDesactive ? <span id={idAide} className="sr-only">{motifDesactive}</span> : null}

      {ouvert ? (
        <div className="absolute left-0 z-30 mt-1 w-[min(22rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
             data-testid={`${testid}-liste`}>
          <div className="flex items-center gap-1.5 border-b px-1.5 pb-1">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input ref={champ} type="text" role="combobox" data-testid={`${testid}-recherche`}
                   aria-label={placeholder} placeholder={placeholder} value={recherche}
                   aria-autocomplete="list" aria-expanded="true" aria-controls={idListe}
                   aria-activedescendant={filtrees[actif] ? idOption(filtrees[actif].id) : undefined}
                   onChange={(e) => setRecherche(e.target.value)} onKeyDown={auClavier}
                   autoComplete="off"
                   className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none" />
          </div>
          <ul id={idListe} role="listbox" aria-label={libelle} className="max-h-64 overflow-y-auto py-1">
            {filtrees.length === 0 ? (
              <li role="option" aria-selected="false" aria-disabled="true" className="px-2 py-1.5 text-sm text-muted-foreground">
                {t('contexte.aucunResultat', 'Aucun résultat pour cette recherche')}
              </li>
            ) : filtrees.map((o, i) => (
              <li key={o.id} id={idOption(o.id)} role="option" aria-selected={o.id === courant}
                  data-testid={`${testid}-option-${o.id}`}
                  data-actif={i === actif ? 'true' : undefined}
                  onMouseEnter={() => setActif(i)}
                  onClick={() => choisir(o.id)}
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent data-[actif=true]:bg-accent">
                <Check className={`size-4 shrink-0 ${o.id === courant ? '' : 'invisible'}`} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {o.detail ? <span className="technical-id shrink-0 text-xs text-muted-foreground">{o.detail}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
